import { cfetch } from '@confluence/network';

/**
 * Minimal shape of the xflow license-information response we rely on. The
 * endpoint returns far more than this; we intentionally type only the fields the
 * Meetings targeting reads so we don't couple to the full (untyped) payload.
 *
 * Editions are lowercase strings, e.g. `'free' | 'standard' | 'premium'` for
 * Confluence/Jira and `'business-ai'` for Loom Business + AI.
 */
export type LicenseInformationProduct = {
	edition?: string | null;
	relatesFromEntitlements?: Array<{ relationshipType?: string | null } | null> | null;
} | null;

export type LicenseInformation = {
	products?: Record<string, LicenseInformationProduct> | undefined;
} | null;

export const CONFLUENCE_PRODUCT_KEY = 'confluence.ondemand';
export const JIRA_PRODUCT_KEY = 'jira-software.ondemand';
export const LOOM_PRODUCT_KEY = 'loom';

const CONFLUENCE_PREMIUM = 'premium';
const JIRA_PREMIUM = 'premium';
const LOOM_BUSINESS_AI = 'business-ai';

/**
 * Fetch the site's license information from the xflow endpoint. This is the
 * sanctioned Confluence way to read per-product editions (mirrors
 * `@confluence/auto-provisioning-edition-parity`'s `fetchLicenseInformation`).
 * A local copy is kept here to avoid a cross-package dependency on a package
 * built for an unrelated feature.
 */
export const fetchLicenseInformation = async (cloudId: string): Promise<LicenseInformation> => {
	const response = await cfetch(`/gateway/api/xflow/${cloudId}/license-information`);
	return response.json();
};

const editionOf = (info: LicenseInformation, productKey: string): string | undefined =>
	info?.products?.[productKey]?.edition ?? undefined;

/** Whether the site has the Loom Business + AI edition. */
export const hasLoomBusinessAi = (info: LicenseInformation): boolean =>
	editionOf(info, LOOM_PRODUCT_KEY) === LOOM_BUSINESS_AI;

/**
 * Whether the site is a full TWC Premium bundle, per the strict definition:
 * Confluence premium AND Jira premium AND Loom Business + AI AND a COLLECTION
 * (bundle) relationship on the Loom product.
 */
export const isTwcPremiumBundle = (info: LicenseInformation): boolean => {
	const confluencePremium = editionOf(info, CONFLUENCE_PRODUCT_KEY) === CONFLUENCE_PREMIUM;
	const jiraPremium = editionOf(info, JIRA_PRODUCT_KEY) === JIRA_PREMIUM;
	const loomBusinessAi = hasLoomBusinessAi(info);
	const inCollection = (info?.products?.[LOOM_PRODUCT_KEY]?.relatesFromEntitlements ?? []).some(
		(relationship) => relationship?.relationshipType === 'COLLECTION',
	);
	return confluencePremium && jiraPremium && loomBusinessAi && inCollection;
};

/**
 * The operative co-use signal: the site has a premium Loom tier. Having Loom
 * Business + AI (standalone or as part of the full TWC Premium bundle) makes a
 * site co-use eligible. `isTwcPremiumBundle` already implies `hasLoomBusinessAi`,
 * so this is effectively "has Loom Business + AI" — kept explicit for clarity
 * and so the strict-bundle intent is documented at the call site.
 */
export const hasPremiumTier = (info: LicenseInformation): boolean =>
	hasLoomBusinessAi(info) || isTwcPremiumBundle(info);
